import React, { useState, useEffect } from 'react';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatSecs, iAxios, timeStats } from '../../lib/utils';
import { useSelector } from 'react-redux';
import * as moment from 'moment';
import classNames from 'classnames';
import Icon from '../ds/Icon';
import { useProjectNames } from '../../lib/hooks';
import { DATA_COLORS, chartTooltip } from '../../lib/chartTheme';

const Segmented = ({ options, value, onChange, label }) => (
    <div className="ds-seg" role="tablist" aria-label={label}>
        {options.map(opt => (
            <button key={opt} type="button" role="tab" aria-selected={value === opt}
                className={`ds-seg__btn${value === opt ? ' is-active' : ''}`} onClick={() => onChange(opt)}>
                {opt}
            </button>
        ))}
    </div>
);

// shared bullet bar: fill = progress %, marker = elapsed %
const Bullet = ({ pct, elapsed, tone }) => (
    <div className="bullet">
        <div className={classNames('bullet__fill', tone && `is-${tone}`)} style={{ width: `${Math.min(100, pct)}%` }} />
        <div className="bullet__marker" style={{ left: `${Math.min(100, elapsed)}%` }} />
    </div>
);

// completion vs elapsed → tone
const toneFor = (pct, elapsed) => {
    if (pct >= elapsed) return 'good';
    if (pct >= elapsed - 20) return 'warn';
    return 'bad';
};

const SprintHealth = ({ sprintStatus, prog }) => {
    const elapsed = Math.min(100, Number(prog) || 0);
    const projectNames = useProjectNames();

    const totals = sprintStatus.reduce((a, p) => ({
        total: a.total + p.total,
        closed: a.closed + (p.total - p.open),
        spent: a.spent + (p.timeSpent || 0),
        est: a.est + (p.timeEstimate || 0),
    }), { total: 0, closed: 0, spent: 0, est: 0 });

    const completion = totals.total ? Math.round(totals.closed * 100 / totals.total) : 0;
    const tone = toneFor(completion, elapsed);
    const behind = Math.round(elapsed - completion);
    const flagText = completion >= elapsed ? 'On track'
        : behind <= 10 ? `Behind by ${behind} pts` : `Behind by ${behind} pts`;

    // most-behind projects first
    const ranked = [...sprintStatus].sort((a, b) => (elapsed - a.perc) - (elapsed - b.perc) === 0
        ? b.total - a.total : (elapsed - b.perc) - (elapsed - a.perc));
    const [showAll, setShowAll] = useState(false);
    const shown = showAll ? ranked : ranked.slice(0, 7);

    return (
        <div>
            <div className="sh-headline">
                <span className="sh-pct">{completion}%<span style={{ fontSize: 'var(--ds-text-md)', fontWeight: 400, color: 'var(--ds-ink-muted)' }}> complete</span></span>
                <span className={`sh-flag is-${tone}`}>{flagText}</span>
            </div>
            <div className="sh-bar bullet">
                <div className={`bullet__fill is-${tone}`} style={{ width: `${completion}%` }} />
                <div className="bullet__marker" style={{ left: `${elapsed}%` }} title={`${elapsed}% of sprint elapsed`} />
            </div>
            <div className="sh-meta">
                <b>{totals.closed}/{totals.total}</b> tasks done · <b>{formatSecs(totals.spent)}</b> logged of {formatSecs(totals.est)} est · {elapsed}% elapsed
            </div>

            <div className="sh-projects">
                <div className="sh-proj-head">
                    <span>Project</span><span>Progress</span><span>Done</span><span>Tasks</span>
                </div>
                {shown.map(p => {
                    const t = toneFor(p.perc, elapsed);
                    const label = projectNames[p.name] || p.name;
                    return (
                        <div className="sh-proj-row" key={p.name}>
                            <span className="sh-proj-name" title={label}>
                                {t === 'bad' && <span className="sh-warn-dot">●</span>}{label}
                            </span>
                            <Bullet pct={p.perc} elapsed={elapsed} tone={t} />
                            <span className="sh-proj-pct">{Math.round(p.perc)}%</span>
                            <span className="sh-proj-num">{p.total - p.open}/{p.total}</span>
                        </div>
                    );
                })}
                {ranked.length > 7 && (
                    <div className="sh-showall">
                        <button type="button" className="ds-btn is-ghost is-sm" onClick={() => setShowAll(!showAll)}>
                            {showAll ? 'Show less' : `Show all ${ranked.length} projects`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const Distribution = ({ sprintStatus, mode }) => {
    const projectNames = useProjectNames();
    const dataKey = mode === 'By time' ? 'timeSpent' : 'total';
    const data = [...sprintStatus].sort((a, b) => b[dataKey] - a[dataKey]).filter(d => d[dataKey] > 0)
        .map(d => ({ ...d, name: projectNames[d.name] || d.name }));
    return (
        <div className="dash-chart is-bounded">
            <ResponsiveContainer width="100%" aspect={1}>
                <PieChart margin={{ top: 16, right: 16, left: 16, bottom: 16 }}>
                    <Pie isAnimationActive={false} data={data} dataKey={dataKey} nameKey="name"
                        innerRadius="52%" outerRadius="80%" paddingAngle={2} cx="50%" cy="50%"
                        stroke="var(--ds-surface)" strokeWidth={2}>
                        {data.map((entry, i) => <Cell key={i} fill={DATA_COLORS[i % DATA_COLORS.length]} />)}
                    </Pie>
                    <Tooltip {...chartTooltip} formatter={mode === 'By time' ? formatSecs : undefined} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export const SprintBacklog = (props) => {
    const { sprintStatus, prog } = props;
    const [view, setView] = useState('Health');

    return (
        <div className="ds-panel">
            <div className="ds-panel__head">
                <h3 className="ds-panel__title">Sprint Health</h3>
                <Segmented options={['Health', 'By tasks', 'By time']} value={view} onChange={setView} label="Sprint view" />
            </div>
            <div className="ds-panel__body">
                {view === 'Health'
                    ? <SprintHealth sprintStatus={sprintStatus} prog={prog} />
                    : <Distribution sprintStatus={sprintStatus} mode={view} />}
            </div>
        </div>
    );
};

export const TodaysProgress = () => {

    const [data, setData] = useState([]);
    const [dateList, setDateList] = useState([]);
    const [datesToShow, setDatesToShow] = useState([]);
    const { userFilter, userInfo } = useSelector(st => ({
        userFilter: st.tickets.userFilter,
        userInfo: st.userInfo
    }));

    const makeDateVisible = (dt) => {
        if (datesToShow.includes(dt)) {
            setDatesToShow(datesToShow.filter(item => item !== dt));
        } else {
            setDatesToShow([...datesToShow, dt]);
        }
    };

    useEffect(() => {
        iAxios.get('/api/v1/tickets/important').then(resp => {
            setData(resp.data.list);
            let closest = 1000000;
            let selected = null;
            const dList = resp.data.list.reduce((agg, item) => {
                const dt = moment(item.dueDate).format('YYYY-MM-DD');
                if (!agg.includes(dt)) {
                    agg.push(dt);
                    const diff = Math.abs(moment().startOf('day').diff(dt, 'mins'));
                    if (diff < closest) { selected = dt; closest = diff; }
                }
                item.dueDate = dt;
                return agg;
            }, []);
            setDateList(dList);
            if (selected) { setDatesToShow([selected]); }
        });
    }, []);

    return (
        <div className="ds-panel">
            <div className="ds-panel__head">
                <h3 className="ds-panel__title">Day Focus</h3>
            </div>
            <div>
                {!dateList && <div className="ds-panel__empty"><span className="ds-spinner" /></div>}
                {dateList.length === 0 && (
                    <div className="ds-panel__empty">Add tasks and check-ins to see data in this section</div>
                )}
                {dateList.map(dt => {
                    const showList = datesToShow.includes(dt);
                    const filtered = data.filter(issue => {
                        if (!userFilter.some(u => u.username === issue.assignee.username)) {
                            return false;
                        }
                        return issue.dueDate === dt;
                    });
                    if (filtered.length === 0) {
                        return null;
                    }
                    const mine = filtered.filter(issue => issue?.assignee?.username === userInfo.username).length;
                    const isFuture = moment(dt).isAfter(moment().format('YYYY-MM-DD'));
                    return (
                        <div className="dayfocus__group" key={`item-${dt}`}>
                            <div className="dayfocus__head">
                                <span className="dayfocus__date">{dt}</span>
                                {mine > 0 && (
                                    <span className={classNames('dayfocus__mine', { 'is-future': isFuture, 'is-due': !isFuture })}>{mine}</span>
                                )}
                                <span className="dayfocus__count">
                                    {filtered.length}
                                    <button
                                        type="button"
                                        className="dayfocus__toggle"
                                        aria-expanded={showList}
                                        aria-label={`Toggle tasks for ${dt}`}
                                        onClick={() => makeDateVisible(dt)}
                                    >
                                        <Icon name="chevronRight" />
                                    </button>
                                </span>
                            </div>
                            {showList && (
                                <div className="dayfocus__items">
                                    {filtered.map(issue => (
                                        <div key={`item-${issue.id}`} className="dayfocus__item">
                                            <a className="dayfocus__item-title" href={issue.url} target="_blank" rel="noreferrer">{issue.title}</a>
                                            <div className="dayfocus__meta">
                                                <span>{timeStats(issue)}</span>
                                                <span>{issue.assignee.name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
